import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify, useDataProvider, useRefresh } from 'react-admin';
import { StandaloneLayout } from '../StandaloneLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Badge as BadgeIcon,
} from '@mui/material';
import { useDriverProfileTabs } from '../hooks/useDriverProfileTabs';
import { DriverProfileTabsPanel } from '../components/DriverProfileTabsPanel';
import { type DriverData } from '../components/driver-profile-tabs';

export const StandaloneDriverProfile = () => {
  const [driverData, setDriverData] = useState<DriverData | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  // Use shared hook for tabs and reference data
  const { tabValue, handleTabChange, licenseTypes, certificationTypes, educationSchools } = useDriverProfileTabs();

  // Load driver data
  useEffect(() => {
    console.log('[StandaloneDriverProfile] Component mounted, loading driver data');
    loadDriver();
  }, []);

  const loadDriver = async () => {
    try {
      console.log('[loadDriver] Starting to load driver data');
      setLoading(true);
      // Use the /drivers/me endpoint to get current user's driver profile
      console.log('[loadDriver] Calling dataProvider.getOne("drivers", { id: "me" })');
      const { data } = await dataProvider.getOne('drivers', { id: 'me' });
      console.log('[loadDriver] Successfully loaded driver data:', data);
      setDriverData(data);
    } catch (error: any) {
      console.error('[loadDriver] Error caught:', error);
      console.error('[loadDriver] Error status:', error.status);
      console.error('[loadDriver] Error response status:', error.response?.status);
      console.error('[loadDriver] Full error object:', JSON.stringify(error, null, 2));

      // Check if it's an authentication error (401/403)
      if (error.status === 401 || error.status === 403 || error.response?.status === 401 || error.response?.status === 403) {
        console.error('[loadDriver] Authentication error detected, redirecting to login');
        notify('Your session has expired. Please log in again.', { type: 'warning' });
        navigate('/login');
        return;
      }

      // Otherwise just log that no driver profile was found
      console.log('[loadDriver] No driver profile found (non-auth error)');
    } finally {
      console.log('[loadDriver] Setting loading to false');
      setLoading(false);
    }
  };

  // Refresh driver data without showing loading state (for tab refreshes)
  const refreshDriver = async () => {
    try {
      console.log('[refreshDriver] Silently refreshing driver data');
      const { data } = await dataProvider.getOne('drivers', { id: 'me' });
      console.log('[refreshDriver] Successfully refreshed driver data:', data);
      setDriverData(data);
    } catch (error: any) {
      console.error('[refreshDriver] Error refreshing driver:', error);
      // Check if it's an authentication error
      if (error.status === 401 || error.status === 403 || error.response?.status === 401 || error.response?.status === 403) {
        notify('Your session has expired. Please log in again.', { type: 'warning' });
        navigate('/login');
        return;
      }
      notify('Error refreshing profile', { type: 'warning' });
    }
  };


  // Handle saving snapshot data
  const handleSaveSnapshot = async (data: Partial<DriverData>) => {
    try {
      await dataProvider.update('drivers', {
        id: driverData?.id,
        data,
        previousData: driverData,
      });
      notify('Profile updated successfully', { type: 'success' });
      // Refresh both the global state and the local driver data
      refresh();
      // Refresh the driver data to show updated changes in the tab
      await refreshDriver();
    } catch (error: any) {
      // Check if it's an authentication error
      if (error.status === 401 || error.status === 403) {
        notify('Your session has expired. Please log in again.', { type: 'warning' });
        navigate('/login');
        return;
      }
      notify('Error updating profile: ' + (error.message || 'Unknown error'), { type: 'error' });
    }
  };

  if (loading) {
    return (
      <StandaloneLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StandaloneLayout>
    );
  }

  if (!driverData) {
    return (
      <StandaloneLayout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 3 }}>Driver Profile</Typography>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <BadgeIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  No Driver Profile Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
                  You haven't created your driver profile yet. Create one to get started!
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<BadgeIcon />}
                  onClick={() => navigate(`/drivers/wizard`)}
                >
                  Create Driver Profile
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </StandaloneLayout>
    );
  }

  return (
    <StandaloneLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Driver Profile</Typography>
        </Box>

        <DriverProfileTabsPanel
          tabValue={tabValue}
          onTabChange={handleTabChange}
          driverData={driverData}
          isLoading={loading}
          licenseTypes={licenseTypes}
          certificationTypes={certificationTypes}
          educationSchools={educationSchools}
          onSaveSnapshot={handleSaveSnapshot}
          onRefreshDriver={refreshDriver}
          isSticky={true}
        />
      </Box>
    </StandaloneLayout>
  );
};
